import {createStyles, Menu as MantineMenu} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Logging from 'util/Logging';

import {buildAndShowGoogleDrivePicker, type GoogleDrivePickerHandle} from './buildGoogleDrivePicker';
import {createUploadDropdownGDrivePickerHandlers} from './CreateUploadDropdownGDrivePickerHandlers';
import {
  GDriveOAuthError,
  GDrivePopupBlockedError,
  GDrivePopupClosedError,
  openGDriveOAuthPopup,
} from './openGDriveOAuthPopup';

// Matches @width-medium--dropdown from less/variables.less, the legacy ui-library Menu medium width.
const DROPDOWN_WIDTH = 300;

interface UploadDropdownButtonProps {
  onLocalUploadClick: () => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  fileId: string;
  multiselect?: boolean;
  disabled?: boolean;
  // Class applied to the underlying Button. Defaults to `u-tiny-spacing-bottom`
  // which provides 5px bottom margin for stacked layouts (e.g. PreviewMixin).
  // Pass an empty string (or a different utility class) when embedding in a
  // flex-centered container like a title bar, where the extra bottom margin
  // throws off vertical alignment.
  buttonClassName?: string;
  // Size of the underlying Button. Defaults to SMALL for stacked preview
  // layouts. Use MINI inside NoteLinkedGroup title bars to match the height
  // of other header action buttons (e.g. RefreshTableButton).
  size?: ButtonSize;
  // Style of the underlying Button. Defaults to SECONDARY.
  buttonStyle?: ButtonStyle;
}

/**
 * Redirect-oriented "Upload a File" dropdown.
 *
 * This is the security-hardened replacement for the callback-oriented
 * UploadDropdownButton, which used the `@googleworkspace/drive-picker-react` web
 * component with OAuth performed in-browser. Here the flow is:
 *
 *   click → openGDriveOAuthPopup() (Benchling backend popup does OAuth, posts the
 *   access token back via postMessage) → buildAndShowGoogleDrivePicker(...) with
 *   the token passed straight into the raw Picker JS API in a CLOSURE ONLY.
 *
 * The picker is shown imperatively (not as a React child), so we keep a ref to
 * the handle returned by buildAndShowGoogleDrivePicker and tear it down via
 * `handle.dispose()` on cancel/pick/error/unmount.
 *
 * The BNCH-158448 fix for the Google Drive 403 is architectural, not the
 * disposal: OAuth now runs on the Benchling backend up front, and we build/show
 * the picker only once the backend delivers an access token. The token's scope
 * is guaranteed by the backend OAuth flow (the frontend does not inspect it); if
 * the user rejects consent (or the backend reports an OAuth error)
 * openGDriveOAuthPopup rejects and we bail before building the picker (see the
 * `accessToken == null` return below) — so, unlike the old in-browser-OAuth
 * flow, we never drive the picker/download without a backend-issued token.
 * Disposing the picker is DOM/lifecycle hygiene (no orphaned iframe or lingering
 * token closure), NOT the 403 fix; deeper stale-iframe hardening is tracked
 * separately in BNCH-161625.
 */
export default function UploadDropdownButton(props: UploadDropdownButtonProps): JSX.Element {
  const {
    onLocalUploadClick,
    onGoogleDriveFilesReady,
    fileId,
    multiselect = false,
    disabled = false,
    buttonClassName = 'u-tiny-spacing-bottom',
    size = ButtonSize.SMALL,
    buttonStyle = ButtonStyle.SECONDARY,
  } = props;

  const {classes} = useStyles();
  // Guards against launching a second OAuth popup / picker while one is already
  // in flight; the handlers below reset it on cancel/pick. Held in a ref, not
  // state, because two synchronous clicks in the same tick must both observe the
  // just-set value: a state update wouldn't commit until the next re-render, so a
  // ref guard is what actually prevents a double-click from opening two popups.
  // It drives no UI, so a ref (no re-render) is also the cheaper representation.
  const isFlowActiveRef = useRef(false);
  const setIsFlowActive = useCallback((active: boolean) => {
    isFlowActiveRef.current = active;
  }, []);

  // Handle to the imperatively-shown picker so we can dispose it (tear down the
  // iframe) on cancel/pick/error. Held in a ref, not state, because the picker
  // is not a React child and disposing must not trigger a re-render.
  const pickerHandleRef = useRef<GoogleDrivePickerHandle | null>(null);

  // Tracks whether the component is still mounted so handleGDriveClick can bail
  // after each await. The unmount cleanup effect alone can't cover a picker that
  // doesn't exist yet: if the component unmounts WHILE the OAuth popup is still
  // open (before buildAndShowGoogleDrivePicker has run), disposePicker() no-ops
  // because pickerHandleRef is still null — then the awaited popup resolves and
  // would mount an orphaned picker iframe into the dead component's ref, which
  // nothing ever disposes. Checking this ref after each await prevents that.
  const isMountedRef = useRef(true);

  // Gate the menu item on the one config value the redirect flow actually needs:
  // AUTH_GOAUTH_PROJECT_NUMBER, which buildAndShowGoogleDrivePicker reads directly
  // as the Picker "app id" and returns null — the picker never opens — when it is
  // empty. Requiring it here means the item only appears when the picker can
  // actually open; otherwise a deploy with it unset would show the item and then
  // fail with a "could not be opened" toast.
  //
  // Note we do NOT gate on the OAuth client id (ELN_GDRIVE_OAUTH_CLIENT_ID): that
  // is the callback path's in-browser OAuth client id. The redirect flow performs
  // OAuth on the Benchling backend (the popup hits /ext/tfi/browser-gdrive-oauth/
  // start), so the client id is irrelevant here and gating on it would wrongly
  // hide the item on tenants that have the backend redirect OAuth configured.
  //
  // We also do NOT re-check ELN_ENABLE_REDIRECT_ORIENTED_GOOGLE_DRIVE_FILE_PICKER
  // here: UploadDropdownButtonSwitch is the single owner of that flag decision and
  // only mounts this component when the flag is on, so a second check would be dead.
  const isRedirectPickerEnabled = Boolean(BenchlingConfig.AUTH_GOAUTH_PROJECT_NUMBER);

  const {handleCanceled, handlePicked} = useMemo(
    () =>
      createUploadDropdownGDrivePickerHandlers({
        setIsFlowActive,
        onGoogleDriveFilesReady,
        fileId,
      }),
    [setIsFlowActive, onGoogleDriveFilesReady, fileId]
  );

  const disposePicker = useCallback(() => {
    pickerHandleRef.current?.dispose();
    pickerHandleRef.current = null;
  }, []);

  // Dispose the picker if the component unmounts mid-flow (e.g. the user
  // navigates away or switches entries after opening the picker but before
  // picking/cancelling). Without this, the imperatively-mounted picker iframe —
  // which lives outside the React tree — would be orphaned in the DOM with no
  // owner (a lingering iframe / token closure). This is lifecycle hygiene, not
  // the 403 fix (that is prevented upstream by bailing when OAuth consent is
  // rejected). `disposePicker` is a stable useCallback, so this effect runs only
  // on unmount.
  useEffect(
    () => () => {
      isMountedRef.current = false;
      disposePicker();
    },
    [disposePicker]
  );

  const handleGDriveClick = useCallback(async () => {
    // Ignore repeat clicks while an OAuth popup / picker flow is already active.
    if (isFlowActiveRef.current) {
      return;
    }
    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.GOOGLE_DRIVE_PICKER_OPENED, {
      fileId,
    });
    setIsFlowActive(true);

    // Handle popup rejections in `.catch` (a method call, not a catch clause) so
    // known error types are consumed without swallowing unforeseen ones behind a
    // bare catch, resolving to a `null` sentinel that stops the flow. The popup
    // only ever resolves with a non-empty token, so `null` unambiguously means
    // "failed".
    const accessToken = await openGDriveOAuthPopup().catch((err: unknown) => {
      setIsFlowActive(false);
      // GDrivePopupClosedError: the user intentionally dismissed the popup — no
      // toast. GDriveOAuthError: openGDriveOAuthPopup already surfaced a toast
      // (with the backend-provided severity/message), so we must not double-toast.
      if (err instanceof GDrivePopupClosedError || err instanceof GDriveOAuthError) {
        return null;
      }
      if (err instanceof GDrivePopupBlockedError) {
        ToastActions.pushToast({
          type: ToastType.WARNING,
          message:
            'The Google Drive authorization popup was blocked. Please allow popups for this site and try again.',
        });
        return null;
      }
      // Any other rejection is unexpected; surface it without leaking token/user
      // data (String(err) only) and show a generic recoverable toast.
      Logging.error('Failed to open the Google Drive authorization popup', {
        extra: {error: String(err)},
      });
      ToastActions.pushToast({
        type: ToastType.DANGER,
        message: 'Something went wrong connecting to Google Drive. Please try again.',
      });
      return null;
    });
    if (accessToken == null) {
      return;
    }
    // If the component unmounted while the OAuth popup was open, bail before
    // building the picker: mounting one now would orphan an iframe the (already
    // run) unmount cleanup effect can no longer dispose. The flow-active guard is
    // irrelevant post-unmount.
    if (!isMountedRef.current) {
      return;
    }

    const handle = await buildAndShowGoogleDrivePicker({
      accessToken,
      multiselect,
      onPicked: (docs) => {
        // Tear the picker down immediately (parity with the old flow's
        // setIsFlowActive(false)), then kick off the async download. Attach a
        // .catch so a download rejection surfaces as telemetry AND a user-facing
        // toast (parity with the popup-error and picker-load-failure paths, which
        // both toast) rather than failing silently or as an unhandled rejection.
        disposePicker();
        handlePicked(docs, accessToken).catch((err: unknown) => {
          Logging.error('Google Drive file download failed', {extra: {error: String(err)}});
          ToastActions.pushToast({
            type: ToastType.DANGER,
            message: 'Failed to download the selected file from Google Drive. Please try again.',
          });
        });
      },
      // Tear the picker iframe down on cancel rather than leaving it mounted —
      // DOM/lifecycle hygiene (parity with the old flow's setIsFlowActive(false),
      // and avoids an orphaned iframe / lingering token closure). This is NOT the
      // 403 fix: the 403 is prevented upstream by bailing when OAuth consent is
      // rejected (the `accessToken == null` return above), so the picker is never
      // built with an under-scoped token.
      onCanceled: () => {
        handleCanceled();
        disposePicker();
      },
      onError: () => {
        setIsFlowActive(false);
        disposePicker();
      },
    });

    if (handle == null) {
      // The Picker API failed to load or is misconfigured; buildAndShowGoogleDrivePicker
      // already emitted the failure telemetry. Reset and offer a retry affordance.
      setIsFlowActive(false);
      ToastActions.pushToast({
        type: ToastType.WARNING,
        message: 'Google Drive could not be opened. Please try again.',
      });
      return;
    }
    // The component may have unmounted during the (awaited) picker build. Storing
    // the handle in the dead component's ref would orphan the iframe — the unmount
    // cleanup effect already ran and saw a null ref — so dispose it here instead.
    if (!isMountedRef.current) {
      handle.dispose();
      return;
    }
    pickerHandleRef.current = handle;
  }, [setIsFlowActive, fileId, multiselect, disposePicker, handleCanceled, handlePicked]);

  return (
    <div className={classes.dropdownContainer}>
      <MantineMenu position="bottom-start" width={DROPDOWN_WIDTH} withinPortal={false}>
        <MantineMenu.Target>
          <span>
            <Button
              buttonStyle={buttonStyle}
              size={size}
              fullWidth={true}
              className={buttonClassName}
              disabled={disabled}
              text="Upload a File"
              isDropdown={true}
              dataTestAttrs={{component: 'UploadDropdownButton', element: 'uploadButton'}}
            />
          </span>
        </MantineMenu.Target>
        <MantineMenu.Dropdown>
          <MantineMenu.Item onClick={onLocalUploadClick}>Upload from computer</MantineMenu.Item>
          {isRedirectPickerEnabled && (
            <MantineMenu.Item onClick={handleGDriveClick}>Upload from Google Drive</MantineMenu.Item>
          )}
        </MantineMenu.Dropdown>
      </MantineMenu>
    </div>
  );
}

const useStyles = createStyles(() => ({
  dropdownContainer: {
    maxWidth: '140px',
    width: '100%',
  },
}));
